import { SecondaryColor } from "../../../../common";

interface Args {
  debouncedData: any[];
  secondXDataKey: string;
  zoomStart: number;
  zoomEnd: number;
  fontSize: number;
  labelFontSize?: number;
}

/**
 * Build the secondary x-axis (date row) config with zoom-aware label density.
 * Returns array (echarts xAxis is always array slot 1+) or empty.
 */
export function buildSecondXAxisConfig({
  debouncedData,
  secondXDataKey,
  zoomStart,
  zoomEnd,
  fontSize,
  labelFontSize,
}: Args) {
  const secondXAxisData = debouncedData.map((item) => item[secondXDataKey]);
  const uniqueDates = [...new Set(secondXAxisData)];
  const totalDates = uniqueDates.length;
  const blocksPerDate = Math.max(
    1,
    Math.floor(debouncedData.length / totalDates),
  );

  const dateRanges: Array<{
    date: any;
    start: number;
    end: number;
    center: number;
  }> = [];
  for (let i = 0; i < secondXAxisData.length; i++) {
    const d = secondXAxisData[i];
    const last = dateRanges[dateRanges.length - 1];
    if (!last || last.date !== d) {
      dateRanges.push({ date: d, start: i, end: i, center: i });
    } else {
      last.end = i;
    }
  }
  for (const r of dateRanges) {
    r.center = Math.floor((r.start + r.end) / 2);
  }
  const indexToGroup = new Map<number, number>();
  dateRanges.forEach((r, gi) => {
    indexToGroup.set(r.center, gi);
  });

  const visibleStart = Math.floor((zoomStart / 100) * debouncedData.length);
  const visibleEnd = Math.min(
    Math.ceil((zoomEnd / 100) * debouncedData.length),
    debouncedData.length - 1,
  );
  const zoomVisibleLength = visibleEnd - visibleStart + 1;
  const visibleDateCount = Math.max(
    1,
    Math.ceil(zoomVisibleLength / blocksPerDate),
  );

  const getSkipFactor = (count: number): number => {
    if (count <= 7) return 1;
    if (count <= 14) return 2;
    if (count <= 30) return 3;
    if (count <= 60) return 5;
    if (count <= 90) return 8;
    if (count <= 180) return 10;
    if (count <= 270) return 14;
    if (count <= 365) return 18;
    if (count <= 450) return 22;
    return 60;
  };

  const lastDateGroupIndex = dateRanges.length - 1;
  const getDateInterval = (index: number): boolean => {
    const groupIndex = indexToGroup.get(index);
    if (groupIndex === undefined) return false;
    if (groupIndex === lastDateGroupIndex) return true;
    if (groupIndex === 0) return true;
    const skipFactor = getSkipFactor(visibleDateCount);
    return groupIndex % skipFactor === 0;
  };

  const dateLabelFontSize =
    visibleDateCount > 100
      ? Math.max(8, fontSize - 2.5)
      : visibleDateCount > 20
        ? fontSize - 1
        : visibleDateCount > 15
          ? fontSize - 0.5
          : fontSize;

  return [
    {
      axisLine: { lineStyle: { color: "#E0E0E0" } },
      type: "category" as const,
      axisPointer: { show: false },
      position: "bottom" as const,
      nameLocation: "end" as const,
      nameTextStyle: {
        fontSize: labelFontSize ?? fontSize + 3,
        color: SecondaryColor,
      },
      axisLabel: {
        color: "#8C8A96",
        interval: getDateInterval,
        fontSize: dateLabelFontSize,
        hideOverlap: false,
      },
      offset: 25,
      data: secondXAxisData,
    },
  ];
}
